import type { HelpTopicDef } from '../types';

export const gettingStarted: HelpTopicDef = {
  slug: 'getting-started',
  title: 'Getting started',
  summary: 'What the portal does, how the pieces fit, and where to do what.',
  group: 'Start here',
  updated: '2026-08-06',
  intro: [
    'This portal turns daily Time & Materials field reports into weekly billing packages. Foremen email their end-of-shift T&M PDFs; the portal reads them, sorts the work into the right change orders (PCOs), builds weekly rollups, and sends the finished package to the general contractor.',
    'Your job in the loop: review incoming work, confirm where it belongs, and send each week when it is ready. Everything mechanical — parsing PDFs, math, spreadsheet generation, emailing — is handled for you.',
  ],
  recipes: [
    {
      task: 'See what needs attention right now',
      steps: [
        'Check the badge on "PDF Inbox" in the left rail — that is how many daily PDFs have work waiting for review.',
        'Open a PDF and work through its blocks: check the lines, confirm the PCO each block belongs to, add any material the report missed, and save.',
        'On a PCO page, look for warning flags on already-sent weeks — a flag means something changed after the week was sent, so the week needs reopening and then finishing again: "Approve & Send" for a corrected copy, or "Close without sending" when the GC does not need one.',
      ],
    },
    {
      task: 'Switch projects',
      steps: [
        'Look just under the Coffman logo in the left rail — that is the project you are working in, shown as its project number and name.',
        'If your login covers more than one project, that panel is a dropdown: click it and pick the project you want.',
        'The portal reloads on the project you picked and stays there until you switch again or sign out. Signing back in starts you on your default project. If you only have one project, the panel is just a label — there is nothing to pick.',
      ],
    },
    {
      task: 'Find a specific PCO',
      steps: [
        'Type a PCO number or name into "Search PCOs" at the top of the left rail (or press Ctrl+K).',
        'Or click "All PCOs" to browse the full directory.',
        'Or open "PCO Log" for the complete ledger — every PCO across four money columns: Proposed, Approved, Submitted, and Remaining.',
      ],
      note: '"Recent PCOs" fills itself from the T&M weekly-rollup PCOs — the ones with weeks to work on. Lump-sum PCOs never turn up there on their own; they live in "All PCOs" and the PCO Log. Pinning is the exception: pin any PCO, either kind, and it holds the top of "Recent PCOs" until you unpin it.',
    },
    {
      task: 'Keep a PCO at the top of the rail',
      steps: [
        'Hover over the PCO — in "Recent PCOs", in search results, or in the "All PCOs" directory — and click the pin that appears in its corner.',
        'The PCO moves to the top of "Recent PCOs" and stays there, whatever the recent activity is, until you unpin it. Pinning from "All PCOs" is the main use: it puts a PCO on the rail that would never arrive on its own — including a lump-sum PCO, which the rail otherwise leaves out entirely.',
        'To unpin, click the filled pin on the row.',
      ],
      note: 'Pins are per project and per browser — pinning on your office computer does not pin on your laptop.',
    },
    {
      task: 'Add a new PCO',
      steps: [
        'Click "Add PCO" on the PCO Log, or "New PCO" at the bottom of the left rail.',
        'Pick how it bills. "T&M Weekly Rollup" is work the foremen will ticket and you will bill week by week through the portal. "Lump-Sum" is a change order priced in the GC’s own paperwork that you are hard-entering here so the PCO Log stays complete. The third option, "Historical Data", is for a PCO that was already being billed before the portal — it takes the past billing off the PCO Log instead of opening this form.',
        'Fill in the PCO number, description, and the funding bucket amount, then click "Create parent PCO".',
      ],
      note: 'Both billing kinds use the same form — the choice only records which sort of PCO it is. See "PCOs & funding" for the full walkthrough, including how to change the kind later if you pick wrong, and "Imported history" for the Historical Data path.',
    },
    {
      task: 'Report a bug or send an idea',
      steps: [
        'Click the small round message button in the bottom-right corner of any screen, or press Ctrl+/ (⌘+/ on a Mac).',
        'Type what you noticed — the panel asks "What did you notice? Bugs, ideas, questions — anything." It records which page you were on for you.',
        'Click "Send note".',
      ],
      note: 'Nothing you type is lost if you close the panel — the draft is held until you send it. Your own past notes are listed under the box.',
    },
  ],
  sections: [
    {
      heading: 'Signing in',
      body: [
        'You sign in with your work email and a password you chose yourself. Nobody at the portal ever sets, sees, or sends a password — a new account arrives as an invitation email, and the link in it is where you pick one. That link is good for three days and works exactly once, so if you need it again ask an administrator to re-send it rather than digging out the old email.',
        'Forgotten it? "Forgot password" on the sign-in page emails you a link that works for an hour. That is also the fastest way back in if too many wrong attempts have made the page ask you to wait — following the emailed link clears the wait immediately.',
        'Too many wrong passwords in a row makes the page pause you for a short while, and the message tells you how long. The wait never grows past fifteen minutes and it clears itself; an administrator can also clear it from "Project Settings" → "Users".',
        'Once you are in, you stay in — signing in on a device keeps you signed in until you sign out. If a laptop or phone goes missing, tell an administrator: "Sign out everywhere" on your row in "Project Settings" → "Users" ends every device at once, and you simply sign in again on the ones you still have.',
      ],
    },
    {
      heading: 'The flow at a glance',
      body: [
        'A foreman emails a daily T&M PDF. The portal parses it into work blocks — labor and equipment per activity. You review each block in the PDF Inbox and confirm which PCO it belongs to. Saved blocks land in that PCO’s weekly rollup. When a week is ready, Approve & Send packages the rollup with its source PDFs and emails it to the general contractor. The PCO Log tracks how much of each PCO’s value has been submitted to the general contractor to date.',
      ],
    },
    {
      heading: 'How the portal is organized',
      body: [
        'The left rail is the map. The panel under the logo names the project you are in. "PDF Inbox" holds incoming daily T&M PDFs waiting for review. "Recent PCOs" lists the T&M weekly-rollup change orders with activity in the last four weeks, with any PCOs you have pinned held at the top; "Search PCOs" and "All PCOs" cover everything else, lump-sum PCOs included. "PCO Log" is the complete billing ledger. "Project Settings" holds employees, equipment, rates, and project details. "Help" is this section.',
        'Each PCO page has its own tabs: the weekly view (rollups grouped by Open, Sent, and Approved), plus cumulative totals and history.',
      ],
    },
  ],
  related: [
    { label: 'The PDF Inbox', to: '/help/inbox' },
    { label: 'Weekly rollups', to: '/help/weekly-rollups' },
    { label: 'Approve & Send', to: '/help/approve-and-send' },
    { label: 'Glossary', to: '/help/glossary' },
  ],
};
